import { Suspense } from "react";
import { useSuspenseQuery } from "@apollo/client";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { Section, SectionHeader } from "@roqua/components/sections";
import { Button } from "@roqua/components/ui/button";

import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageTitle } from "@/components/PageTitle";
import { Timeline } from "@/components/Timeline";
import { Activities } from "@/features/Timeline/Activities";
import { FillOutMoment } from "@/features/Timeline/FillOutMoment";
import { sortFillOutData, transformDossierFillOutTasks } from "@/features/Timeline/helpers";
import { ProtocolSubscriptions } from "@/features/Timeline/ProtocolSubscriptions";
import { SelfInitiableMeasuredQuestionnaires } from "@/features/Timeline/SelfInitiableMeasuredQuestionnaires";
import { TimelinePageQuery } from "@/features/Timeline/TimelinePageQuery";
import { useSessionContext } from "@/session";

export const Route = createFileRoute("/$organizationKey/$dossierId/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { organizationKey, dossierId } = useSessionContext();
  const { data } = useSuspenseQuery(TimelinePageQuery, {
    variables: { dossierId },
  });

  const { t } = useTranslation(["Timeline", "common"]);

  const fillOutData = transformDossierFillOutTasks(data.dossier.fillOutTasks);
  const sortedFillOutData = sortFillOutData(fillOutData);

  const hasSelfInitiableQuestionnaires = data.dossier.respondents.some(
    (respondent) => respondent.selfInitiableMeasuredQuestionnaires.nodes.length > 0,
  );
  const hasProtocolSubscriptions = data.dossier.respondents.some(
    (respondent) => respondent.protocolSubscriptions.nodes.length > 0,
  );

  return (
    <>
      <PageTitle title="timeline" />
      <Section variant="header">
        <SectionHeader
          header={t("pending.header")}
          buttons={
            sortedFillOutData.length > 0 && (
              <Button className="rounded-full" variant="outline" asChild>
                <Link
                  to="/$organizationKey/$dossierId/pending"
                  params={{ organizationKey, dossierId }}
                >
                  {t("pending.manageButton")}
                </Link>
              </Button>
            )
          }
        />

        {sortedFillOutData.length === 0 && <div>{t("pending.nothingPending")}</div>}

        {sortedFillOutData.length > 0 && (
          <Timeline id="pending-responses">
            {sortedFillOutData.map(({ respondent, fillOutMoment }, idx) => (
              <FillOutMoment
                key={fillOutMoment.id}
                respondent={respondent}
                fillOutMoment={fillOutMoment}
                last={idx === sortedFillOutData.length - 1}
              />
            ))}
          </Timeline>
        )}
      </Section>

      <Section variant="middle">
        {hasProtocolSubscriptions && (
          <ProtocolSubscriptions respondents={data.dossier.respondents} />
        )}
        {hasSelfInitiableQuestionnaires && (
          <SelfInitiableMeasuredQuestionnaires respondents={data.dossier.respondents} />
        )}
      </Section>

      <Section>
        <SectionHeader header={t("recents.header")} />

        <Suspense>
          <ErrorBoundary>
            <Activities />
          </ErrorBoundary>
        </Suspense>
      </Section>
    </>
  );
}
